import styled from 'styled-components';

import {
  UI_SHAPE,
  UiShape,
} from '@savgroup-front-common/constants/shared/shape';
import { UI_SIZE, UiSize } from '@savgroup-front-common/constants/shared/size';
import { darken } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';
import { DefaultStyledProps } from '@savgroup-front-common/types';

import { getThemeFromProps } from '../../theme/helpers';
import { fontStyle } from '../../theme/helpers/fontStyle';

import {
  TAG_TEXT_TRANSFORM,
  TAG_VARIANT,
  TagTextTransform,
  TagVariant,
} from './Tag.types';

export type { TagTextTransform, TagVariant } from './Tag.types';

type InternalVariant = TagVariant | 'disabled';

type ColorProps = {
  $variant?: TagVariant;
  theme?: DefaultStyledProps['theme'];
  disabled?: boolean;
  primary?: boolean;
  secondary?: boolean;
  success?: boolean;
  alert?: boolean;
  danger?: boolean;
  neutral?: boolean;
};

const legacyVariant = (props: ColorProps): TagVariant => {
  if (props.$variant) {
    return props.$variant;
  }
  if (props.primary) {
    return TAG_VARIANT.PRIMARY;
  }
  if (props.secondary) {
    return TAG_VARIANT.SECONDARY;
  }
  if (props.success) {
    return TAG_VARIANT.SUCCESS;
  }
  if (props.danger) {
    return TAG_VARIANT.DANGER;
  }
  if (props.alert) {
    return TAG_VARIANT.ALERT;
  }
  if (props.neutral) {
    return TAG_VARIANT.NEUTRAL;
  }

  return TAG_VARIANT.DEFAULT;
};

const resolveColors = (props: ColorProps) => {
  const theme = getThemeFromProps(props);

  let variant: InternalVariant = legacyVariant(props);

  if (props.disabled) {
    variant = 'disabled';
  }

  switch (variant) {
    case 'disabled':
      return {
        bgColor: props.theme?.newUI.tagsColors?.disabled.bgColor,
        color: theme.newUI.tagsColors?.disabled.color,
      };
    case TAG_VARIANT.NEUTRAL:
      return {
        bgColor: props.theme?.newUI.tagsColors?.neutral.bgColor,
        color: theme.newUI.tagsColors?.neutral.color,
      };
    case TAG_VARIANT.DANGER:
      return {
        bgColor: props.theme?.newUI.tagsColors?.danger.bgColor,
        color: theme.newUI.tagsColors?.danger.color,
      };
    case TAG_VARIANT.SUCCESS:
      return {
        bgColor: props.theme?.newUI.tagsColors?.success.bgColor,
        color: theme.newUI.tagsColors?.success.color,
      };
    case TAG_VARIANT.PRIMARY:
      return {
        bgColor: props.theme?.newUI.tagsColors?.primary.bgColor,
        color: theme.newUI.tagsColors?.primary.color,
      };
    case TAG_VARIANT.SECONDARY:
      return {
        bgColor: props.theme?.newUI.tagsColors?.secondary.bgColor,
        color: theme.newUI.tagsColors?.secondary.color,
      };
    case TAG_VARIANT.GOLD:
      return {
        bgColor: props.theme?.newUI.tagsColors?.gold?.bgColor,
        color: theme.newUI.tagsColors?.gold?.color,
      };
    case TAG_VARIANT.DEFAULT:
      return {
        bgColor: theme.newUI.tagsColors?.alert.bgColor,
        color: theme.newUI.tagsColors?.alert.color,
      };
    case TAG_VARIANT.ALERT:
    default:
      return {
        bgColor: props.theme?.newUI.tagsColors?.alert.bgColor,
        color: theme.newUI.tagsColors?.alert.color,
      };
  }
};

export const backgroundColorBasedOnProps = (props: ColorProps) =>
  resolveColors(props).bgColor;

export const colorBasedOnProps = (props: ColorProps) =>
  resolveColors(props).color;

const SIZE_STYLES: Partial<
  Record<
    UiSize,
    {
      padding?: string;
      maxHeight?: string;
      height?: string;
    }
  >
> = {
  [UI_SIZE.XS]: {
    padding: `${spacing('xs')} ${spacing(1.5)}`,
    maxHeight: spacing(5),
    height: spacing(5),
  },
  [UI_SIZE.SM]: {
    padding: `${spacing('xs')} ${spacing(2.5)}`,
    maxHeight: spacing('xxl'),
    height: spacing(7),
  },
  [UI_SIZE.MD]: {
    padding: `${spacing('xs')} ${spacing(2.5)}`,
    maxHeight: spacing('xxl'),
    height: undefined,
  },
};

interface $TagProps extends DefaultStyledProps {
  disabled?: boolean;
  $withoutMargin?: boolean;
  $noEllispis?: boolean;
  $fluid?: boolean;
  $isNaked: boolean;
  $size: UiSize;
  $variant: TagVariant;
  $shape: UiShape;
  $textTransform: TagTextTransform;
  onClick?: () => void;
  $isHollow?: boolean;
  hasZIndex?: boolean;
}

export const $Tag = styled.div<$TagProps>`
  width: ${({ $fluid }) => ($fluid ? null : 'fit-content')};
  min-width: 0;
  max-height: ${({ $noEllispis, $size }) =>
    $noEllispis ? null : SIZE_STYLES[$size]?.maxHeight};
  padding: ${({ $isNaked, $size }) =>
    $isNaked ? `${spacing('xs')} 0` : SIZE_STYLES[$size]?.padding};
  margin: 0;
  gap: ${spacing('sm')};
  margin-bottom: ${({ $withoutMargin }) =>
    $withoutMargin ? null : spacing('sm')};
  ${fontStyle('xxs')};
  font-weight: ${fontWeight('bold')};
  display: inline-flex;
  flex-shrink: 0;
  white-space: ${({ $noEllispis }) => ($noEllispis ? null : 'nowrap')};
  overflow: ${({ $noEllispis }) => ($noEllispis ? null : 'hidden')};
  text-overflow: ${({ $noEllispis }) => ($noEllispis ? null : 'ellipsis')};
  font-style: normal;
  text-align: ${({ $noEllispis }) => ($noEllispis ? 'left' : 'center')};
  border: 1px solid;
  z-index: ${({ hasZIndex }) => (hasZIndex ? 20 : 'auto')};
  border-color: ${({ theme, $isHollow, ...rest }) =>
    $isHollow
      ? backgroundColorBasedOnProps({ theme, ...rest })
      : 'transparent'};
  background-color: ${({ theme, $isHollow, $isNaked, ...rest }) => {
    if ($isHollow || $isNaked) {
      return 'transparent';
    }

    return backgroundColorBasedOnProps({ theme, ...rest });
  }};
  border-radius: ${({ theme, $shape }) =>
    $shape === UI_SHAPE.ROUND ? rem(999) : theme.borders.radius};

  &:hover {
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
    background-color: ${({ theme, onClick, $isNaked, ...rest }) => {
      if (onClick) {
        const color = $isNaked
          ? 'transparent'
          : backgroundColorBasedOnProps({ theme, ...rest });

        return darken(0.08, color);
      }

      return undefined;
    }};
  }

  color: ${colorBasedOnProps};
  transition-duration: 0.3s;

  ${({ $textTransform }) => {
    const transform = $textTransform ?? TAG_TEXT_TRANSFORM.UPPERCASE;

    if (transform === TAG_TEXT_TRANSFORM.UPPERFIRST) {
      return `
        text-transform: none;
        & > span:first-child::first-letter { text-transform: uppercase; }
      `;
    }

    return `text-transform: ${transform};`;
  }};
  align-items: center;
  position: relative;
  height: ${({ $size }) => SIZE_STYLES[$size]?.height};

  & > span {
    overflow: ${({ $noEllispis }) => ($noEllispis ? 'visible' : 'hidden')};
    text-overflow: ${({ $noEllispis }) => ($noEllispis ? 'clip' : 'ellipsis')};
    white-space: ${({ $noEllispis }) => ($noEllispis ? 'normal' : 'nowrap')};
    overflow-wrap: ${({ $noEllispis }) =>
      $noEllispis ? 'anywhere' : 'normal'};
    word-break: ${({ $noEllispis }) => ($noEllispis ? 'break-word' : 'normal')};
  }
`;

export const $TagCloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const $TagContent = styled.span<{
  $isClosable?: boolean;
  onClick?: (event: React.MouseEvent) => void;
}>`
  display: block;
  flex-grow: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  justify-content: ${({ $isClosable }) =>
    $isClosable ? 'flex-start' : 'center'};
  ${({ onClick }) => (onClick ? 'cursor: pointer;' : '')}
`;
